import { Link, useSearchParams } from "react-router-dom";
import DatePicker from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getAdminContents,
  getAdminContentById,
  createAdminContent,
  updateAdminContent,
  deleteAdminContent,
  uploadAdminImage,
} from "../../api/adminApi";

const Font = Quill.import("formats/font");

Font.whitelist = ["vazirmatn", "tahoma", "yekan", "shabnam", "sahel"];

Quill.register(Font, true);


const MIN_IMAGE_WIDTH_PX = 80;
const MAX_IMAGE_WIDTH_PX = 1400;
const MIN_IMAGE_WIDTH_PERCENT = 10;
const MAX_IMAGE_WIDTH_PERCENT = 100;
const MIN_IMAGE_HEIGHT_PX = 40;
const MAX_IMAGE_HEIGHT_PX = 1200;
const MIN_IMAGE_BORDER_PX = 0;
const MAX_IMAGE_BORDER_PX = 40;
const DEFAULT_IMAGE_BORDER_COLOR = "#cbd5e1";

const clampNumber = (value, min, max) => {
  const number = Number(value);

  if (!Number.isFinite(number)) return min;

  return Math.min(Math.max(number, min), max);
};

const normalizeImageWidth = (value, fallback = "100%") => {
  const rawValue = String(value || "").trim().toLowerCase();
  const rawFallback = String(fallback || "100%").trim().toLowerCase();
  const source = rawValue || rawFallback;
  const match = source.match(/^(\d+(?:\.\d+)?)(px|%)?$/);

  if (!match) {
    const fallbackMatch = rawFallback.match(/^(\d+(?:\.\d+)?)(px|%)?$/);

    if (!fallbackMatch) return "100%";

    const fallbackUnit = fallbackMatch[2] || "%";
    const fallbackAmount = Number(fallbackMatch[1]);

    if (fallbackUnit === "%") {
      return `${clampNumber(fallbackAmount, MIN_IMAGE_WIDTH_PERCENT, MAX_IMAGE_WIDTH_PERCENT)}%`;
    }

    return `${Math.round(clampNumber(fallbackAmount, MIN_IMAGE_WIDTH_PX, MAX_IMAGE_WIDTH_PX))}px`;
  }

  const amount = Number(match[1]);
  const unit = match[2] || "%";

  if (unit === "%") {
    return `${clampNumber(amount, MIN_IMAGE_WIDTH_PERCENT, MAX_IMAGE_WIDTH_PERCENT)}%`;
  }

  return `${Math.round(clampNumber(amount, MIN_IMAGE_WIDTH_PX, MAX_IMAGE_WIDTH_PX))}px`;
};

const splitImageWidth = (value, fallback = "100%") => {
  const normalized = normalizeImageWidth(value, fallback);
  const match = normalized.match(/^(\d+(?:\.\d+)?)(px|%)$/);

  return {
    amount: match ? match[1] : "100",
    unit: match ? match[2] : "%",
    width: normalized,
  };
};

const normalizeImageHeight = (value, fallback = "auto") => {
  const rawValue = String(value ?? "").trim().toLowerCase();
  const rawFallback = String(fallback ?? "auto").trim().toLowerCase();
  const source = rawValue || rawFallback;

  if (!source || source === "auto") {
    return "auto";
  }

  const match = source.match(/^(\d+(?:\.\d+)?)(px)?$/);

  if (!match) {
    return rawFallback && rawFallback !== source
      ? normalizeImageHeight(rawFallback, "auto")
      : "auto";
  }

  return `${Math.round(clampNumber(Number(match[1]), MIN_IMAGE_HEIGHT_PX, MAX_IMAGE_HEIGHT_PX))}px`;
};

const normalizeImageBorderWidth = (value, fallback = "0") => {
  const rawValue = String(value ?? "").trim().toLowerCase();
  const rawFallback = String(fallback ?? "0").trim().toLowerCase();
  const source = rawValue || rawFallback;
  const match = source.match(/^(\d+(?:\.\d+)?)(px)?$/);

  if (!match) return 0;

  return Math.round(clampNumber(Number(match[1]), MIN_IMAGE_BORDER_PX, MAX_IMAGE_BORDER_PX));
};

const buildSafeImageStyle = ({ width, height = "auto", borderWidth = 0 } = {}) => {
  const normalizedWidth = normalizeImageWidth(width, "100%");
  const normalizedHeight = normalizeImageHeight(height, "auto");
  const normalizedBorderWidth = normalizeImageBorderWidth(borderWidth, "0");
  const heightRule =
    normalizedHeight === "auto"
      ? "height: auto !important;"
      : `height: ${normalizedHeight} !important;`;
  const borderRule =
    normalizedBorderWidth > 0
      ? `border: ${normalizedBorderWidth}px solid ${DEFAULT_IMAGE_BORDER_COLOR} !important;`
      : "border: 0 !important;";

  return [
    `width: ${normalizedWidth} !important;`,
    "max-width: 100% !important;",
    heightRule,
    "object-fit: contain !important;",
    "box-sizing: border-box !important;",
    borderRule,
  ].join(" ");
};

const getImageWidthFromNode = (node, fallback = "100%") => {
  if (!node) return normalizeImageWidth(fallback, "100%");

  const explicitWidth =
    node.getAttribute("data-img-width") ||
    node.style?.width ||
    node.getAttribute("width") ||
    "";

  if (explicitWidth) {
    return normalizeImageWidth(explicitWidth, fallback);
  }

  return normalizeImageWidth(fallback, "100%");
};

const getImageHeightFromNode = (node, fallback = "auto") => {
  if (!node) return normalizeImageHeight(fallback, "auto");

  const explicitHeight =
    node.getAttribute("data-img-height") ||
    node.style?.height ||
    node.getAttribute("height") ||
    "";

  if (explicitHeight) {
    return normalizeImageHeight(explicitHeight, fallback);
  }

  return normalizeImageHeight(fallback, "auto");
};

const getImageBorderWidthFromNode = (node, fallback = "0") => {
  if (!node) return normalizeImageBorderWidth(fallback, "0");

  const explicitBorderWidth =
    node.getAttribute("data-img-border") ||
    node.style?.borderWidth ||
    "";

  return normalizeImageBorderWidth(explicitBorderWidth, fallback);
};

const getImageSettingsFromNode = (node) => ({
  width: getImageWidthFromNode(node),
  height: getImageHeightFromNode(node),
  borderWidth: getImageBorderWidthFromNode(node),
});

const applyImageSettingsToNode = (node, settings = {}) => {
  if (!node) return;

  const normalizedWidth = normalizeImageWidth(settings.width, getImageWidthFromNode(node));
  const normalizedHeight = normalizeImageHeight(settings.height, getImageHeightFromNode(node));
  const normalizedBorderWidth = normalizeImageBorderWidth(
    settings.borderWidth,
    getImageBorderWidthFromNode(node)
  );
  const split = splitImageWidth(normalizedWidth);

  node.classList.add("article-editor-image", "article-img-custom");
  node.setAttribute("data-img-width", normalizedWidth);
  node.setAttribute("data-img-height", normalizedHeight);
  node.setAttribute("data-img-border", String(normalizedBorderWidth));
  node.setAttribute(
    "style",
    buildSafeImageStyle({
      width: normalizedWidth,
      height: normalizedHeight,
      borderWidth: normalizedBorderWidth,
    })
  );

  if (split.unit === "px") {
    node.setAttribute("width", String(Math.round(Number(split.amount))));
  } else {
    node.removeAttribute("width");
  }

  if (normalizedHeight.endsWith("px")) {
    node.setAttribute("height", normalizedHeight.replace("px", ""));
  } else {
    node.removeAttribute("height");
  }
};

const applyImageWidthToNode = (node, width = "100%") => {
  if (!node) return;

  applyImageSettingsToNode(node, {
    ...getImageSettingsFromNode(node),
    width,
  });
};

const BaseImage = Quill.import("formats/image");

class ResizableImageBlot extends BaseImage {
  static blotName = "image";
  static tagName = "img";

  static create(value) {
    const imageValue = typeof value === "object" && value !== null ? value : {};
    const src = typeof value === "string" ? value : imageValue.src || imageValue.url || "";
    const node = super.create(src);

    applyImageSettingsToNode(node, {
      width: imageValue.width || imageValue.size || "100%",
      height: imageValue.height || "auto",
      borderWidth: imageValue.borderWidth || imageValue.border || 0,
    });

    if (imageValue.alt) {
      node.setAttribute("alt", imageValue.alt);
    }

    return node;
  }

  static value(node) {
    const settings = getImageSettingsFromNode(node);

    return {
      src: node.getAttribute("src") || "",
      alt: node.getAttribute("alt") || "",
      width: settings.width,
      height: settings.height,
      borderWidth: settings.borderWidth,
    };
  }
}

Quill.register(ResizableImageBlot, true);


const emptyForm = {
  title: "",
  type: "news",
  category: "",
  tags: "",
  summary: "",
  content: "",
  image: "",
  status: "draft",
  level: "",
  duration: "",
  publishAt: "",
};

const newsCategories = [
  "تکنولوژی",
  "دانستنی‌ها",
  "نرم افزار",
  "سخت افزار",
  "شبکه",
  "موبایل",
  "لپ تاپ",
  "هوش مصنوعی",
];

const courseCategories = [
  "شبکه",
  "امنیت",
  "برنامه نویسی",
  "موبایل",
  "طراحی سایت",
];

const courseLevels = ["مقدماتی", "متوسط", "پیشرفته"];

const statusOptions = [
  { value: "draft", label: "پیش‌نویس" },
  { value: "published", label: "منتشر شده" },
  { value: "scheduled", label: "انتشار زمان‌بندی‌شده" },
];

const editorToolbar = [
    [{ font: Font.whitelist }],
    [{ header: [1, 2, 3, 4, false] }],
    [{ size: ["small", false, "large", "huge"] }],

    ["bold", "italic", "underline", "strike"],

    [{ color: [] }, { background: [] }],

    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],

    [{ align: [] }],
    [{ direction: "rtl" }],

    ["link", "image"],
    ["clean"],
  ];

const editorFormats = [
  "font",
  "header",
  "size",

  "bold",
  "italic",
  "underline",
  "strike",

  "color",
  "background",

  "list",
  "bullet",
  "indent",

  "align",
  "direction",

  "link",
  "image",
];

const editorEmojis = [
  "😀",
  "😊",
  "😍",
  "🤩",
  "🔥",
  "✨",
  "⭐",
  "✅",
  "❌",
  "⚠️",
  "📌",
  "📢",
  "💡",
  "🚀",
  "🎯",
  "💻",
  "📱",
  "🧠",
  "🔐",
  "🌐",
  "📚",
  "🎓",
  "🛠️",
];

const formatDateTimeLocal = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60 * 1000);

  return localDate.toISOString().slice(0, 16);
};

const formatPersianDateTime = (value) => {
  if (!value) return "بدون زمان";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "بدون زمان";

  return date.toLocaleString("fa-IR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function CustomSelect({ name, value, options, placeholder, onChange }) {
  const [open, setOpen] = useState(false);

  const selectedOption = options.find((item) => item.value === value);

  const handleSelect = (option) => {
    onChange({
      target: {
        name,
        value: option.value,
      },
    });

    setOpen(false);
  };

  const toggleOpen = () => {
    setOpen((prev) => !prev);
  };

  return (
    <div
      className={`admin-custom-select ${open ? "open" : ""}`}
      style={{
        position: "relative",
        zIndex: open ? 999999 : 20,
      }}
    >
      <div
        className="admin-custom-select-trigger"
        role="button"
        tabIndex={0}
        onClick={toggleOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleOpen();
          }
        }}
      >
        <span className="admin-custom-select-label-force">
          {selectedOption ? selectedOption.label : placeholder}
        </span>

        <span className="admin-custom-select-arrow-force">⌄</span>
      </div>

      {open && (
        <div
          className="admin-custom-select-menu"
          style={{
            position: "absolute",
            zIndex: 1000000,
          }}
        >
          {options.map((option) => {
            const isActive = value === option.value;

            return (
              <div
                key={option.value}
                className={
                  isActive
                    ? "admin-custom-select-item active"
                    : "admin-custom-select-item"
                }
                role="button"
                tabIndex={0}
                onClick={() => handleSelect(option)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelect(option);
                  }
                }}
              >
                <span className="admin-custom-select-label-force">
                  {option.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
const cleanEditorHtml = (html) => {
  if (!html) return "";

  const baseHtml = String(html)
    .replace(/<font[^>]*>/g, "")
    .replace(/<\/font>/g, "")
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<o:p>\s*<\/o:p>/g, "")
    .replace(/(<br\s*\/?>\s*){3,}/g, "<br/><br/>")
    .trim();

  if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
    return baseHtml;
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(`<div>${baseHtml}</div>`, "text/html");
  const wrapper = doc.body.firstElementChild;

  if (!wrapper) return baseHtml;

  const allowedClassPrefixes = [
    "ql-align-",
    "ql-direction-",
    "ql-indent-",
    "ql-font-",
    "ql-size-",
  ];

  const allowedExactClasses = new Set([
    "article-editor-image",
    "article-img-custom",
  ]);

  wrapper.querySelectorAll(".article-image-grid").forEach((grid) => {
    const fragment = doc.createDocumentFragment();

    grid.querySelectorAll("img").forEach((img, index) => {
      if (index > 0) {
        fragment.appendChild(doc.createTextNode(" "));
      }

      fragment.appendChild(img.cloneNode(true));
    });

    grid.replaceWith(fragment);
  });

  wrapper.querySelectorAll("*").forEach((node) => {
    node.removeAttribute("contenteditable");

    Array.from(node.attributes).forEach((attr) => {
      if (/^on/i.test(attr.name)) {
        node.removeAttribute(attr.name);
      }
    });

    if (node.hasAttribute("class")) {
      const safeClasses = Array.from(node.classList).filter(
        (className) =>
          allowedExactClasses.has(className) ||
          allowedClassPrefixes.some((prefix) => className.startsWith(prefix))
      );

      if (safeClasses.length) {
        node.setAttribute("class", safeClasses.join(" "));
      } else {
        node.removeAttribute("class");
      }
    }

    if (node.tagName === "IMG") {
      applyImageSettingsToNode(node, getImageSettingsFromNode(node));
      return;
    }

    node.removeAttribute("style");
    node.removeAttribute("data-img-width");
    node.removeAttribute("data-img-height");
    node.removeAttribute("data-img-border");
    node.removeAttribute("width");
    node.removeAttribute("height");
  });

  return wrapper.innerHTML.trim();
};

function AdminContentManager() {
  const [contents, setContents] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [activeImageMeta, setActiveImageMeta] = useState(null);
  const [imageWidthInput, setImageWidthInput] = useState("100");
  const [imageWidthUnit, setImageWidthUnit] = useState("%");
  const [imageHeightInput, setImageHeightInput] = useState("");
  const [imageBorderInput, setImageBorderInput] = useState("0");
  const [resizeOverlay, setResizeOverlay] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);

  const quillRef = useRef(null);
  const richEditorRef = useRef(null);
  const activeImageRef = useRef(null);
  const resizeFrameRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const editIdFromArchive = searchParams.get("edit");

  const currentCategories =
    formData.type === "news" ? newsCategories : courseCategories;

  const visibleContents = contents.slice(0, 5);

  const updateResizeOverlay = useCallback(() => {
    const img = activeImageRef.current;
    const container = richEditorRef.current;

    if (!img || !container || !img.isConnected) {
      setResizeOverlay(null);
      return;
    }

    const imgRect = img.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    setResizeOverlay({
      left: imgRect.left - containerRect.left + container.scrollLeft,
      top: imgRect.top - containerRect.top + container.scrollTop,
      width: imgRect.width,
      height: imgRect.height,
    });
  }, []);

  const scheduleResizeOverlayUpdate = useCallback(() => {
    if (resizeFrameRef.current) {
      window.cancelAnimationFrame(resizeFrameRef.current);
    }

    resizeFrameRef.current = window.requestAnimationFrame(() => {
      updateResizeOverlay();
      resizeFrameRef.current = null;
    });
  }, [updateResizeOverlay]);

  const syncEditorContentFromDom = useCallback(() => {
    const editor = quillRef.current?.getEditor?.();

    if (!editor?.root) return;

    setFormData((prev) => ({
      ...prev,
      content: editor.root.innerHTML,
    }));
  }, []);

  const clearActiveImage = useCallback(() => {
    if (activeImageRef.current) {
      activeImageRef.current.classList.remove("is-selected");
    }

    activeImageRef.current = null;
    setActiveImageMeta(null);
    setResizeOverlay(null);
  }, []);

  const selectEditorImage = useCallback(
    (img) => {
      if (!img) return;

      if (activeImageRef.current && activeImageRef.current !== img) {
        activeImageRef.current.classList.remove("is-selected");
      }

      const settings = getImageSettingsFromNode(img);
      const split = splitImageWidth(settings.width);

      applyImageSettingsToNode(img, settings);
      img.classList.add("is-selected");
      activeImageRef.current = img;
      setImageWidthInput(split.amount);
      setImageWidthUnit(split.unit);
      setImageHeightInput(settings.height === "auto" ? "" : settings.height.replace("px", ""));
      setImageBorderInput(String(settings.borderWidth));
      setActiveImageMeta({
        src: img.getAttribute("src") || "",
        width: split.width,
        widthAmount: split.amount,
        widthUnit: split.unit,
        height: settings.height,
        borderWidth: settings.borderWidth,
      });

      const editor = quillRef.current?.getEditor?.();
      const imageBlot = Quill.find(img);

      if (editor && imageBlot) {
        const imageIndex = editor.getIndex(imageBlot);
        editor.setSelection(imageIndex + 1, 0, "silent");
      }

      scheduleResizeOverlayUpdate();
    },
    [scheduleResizeOverlayUpdate]
  );

  const handleEditorAreaClick = useCallback(
    (event) => {
      const target = event.target;

      if (target?.closest?.(".admin-image-resize-overlay")) {
        return;
      }

      const img = target?.closest?.(".ql-editor img");
      const editorRoot = target?.closest?.(".ql-editor");

      if (img) {
        selectEditorImage(img);
        return;
      }

      if (editorRoot) {
        clearActiveImage();
      }
    },
    [clearActiveImage, selectEditorImage]
  );

  const applyActiveImageSettings = useCallback(
    (settings, successMessage = "تنظیمات عکس اعمال شد.") => {
      const img = activeImageRef.current;

      if (!img) {
        setError("ابتدا روی یکی از عکس‌های داخل متن کلیک کنید.");
        return;
      }

      const currentSettings = getImageSettingsFromNode(img);
      const normalizedSettings = {
        width: normalizeImageWidth(settings.width, currentSettings.width),
        height: normalizeImageHeight(settings.height, currentSettings.height),
        borderWidth: normalizeImageBorderWidth(settings.borderWidth, currentSettings.borderWidth),
      };
      const split = splitImageWidth(normalizedSettings.width);

      applyImageSettingsToNode(img, normalizedSettings);
      setImageWidthInput(split.amount);
      setImageWidthUnit(split.unit);
      setImageHeightInput(
        normalizedSettings.height === "auto" ? "" : normalizedSettings.height.replace("px", "")
      );
      setImageBorderInput(String(normalizedSettings.borderWidth));
      setActiveImageMeta((prev) => ({
        ...(prev || { src: img.getAttribute("src") || "" }),
        width: split.width,
        widthAmount: split.amount,
        widthUnit: split.unit,
        height: normalizedSettings.height,
        borderWidth: normalizedSettings.borderWidth,
      }));
      scheduleResizeOverlayUpdate();
      syncEditorContentFromDom();
      setMessage(successMessage);
      setError("");
    },
    [scheduleResizeOverlayUpdate, syncEditorContentFromDom]
  );

  const applyManualImageSettings = useCallback(() => {
    const widthAmount = Number(imageWidthInput);
    const heightText = String(imageHeightInput || "").trim();
    const heightAmount = heightText ? Number(heightText) : null;
    const borderAmount = Number(imageBorderInput);

    if (!Number.isFinite(widthAmount) || widthAmount <= 0) {
      setError("عدد عرض تصویر معتبر نیست.");
      return;
    }

    if (heightText && (!Number.isFinite(heightAmount) || heightAmount <= 0)) {
      setError("عدد ارتفاع تصویر معتبر نیست. برای ارتفاع خودکار، فیلد ارتفاع را خالی بگذارید.");
      return;
    }

    if (!Number.isFinite(borderAmount) || borderAmount < 0) {
      setError("عدد ضخامت کادر معتبر نیست.");
      return;
    }

    const normalizedWidth = normalizeImageWidth(`${widthAmount}${imageWidthUnit}`);
    const normalizedHeight = heightText ? normalizeImageHeight(`${heightAmount}px`) : "auto";
    const normalizedBorderWidth = normalizeImageBorderWidth(`${borderAmount}px`);
    const widthLabel =
      imageWidthUnit === "%" ? `${splitImageWidth(normalizedWidth).amount}٪` : normalizedWidth;
    const heightLabel = normalizedHeight === "auto" ? "خودکار" : normalizedHeight;

    applyActiveImageSettings(
      {
        width: normalizedWidth,
        height: normalizedHeight,
        borderWidth: normalizedBorderWidth,
      },
      `عکس با عرض ${widthLabel}، ارتفاع ${heightLabel} و کادر ${normalizedBorderWidth}px تنظیم شد.`
    );
  }, [applyActiveImageSettings, imageBorderInput, imageHeightInput, imageWidthInput, imageWidthUnit]);

  const handleImageResizePointerDown = useCallback(
    (event) => {
      const img = activeImageRef.current;
      const editorRoot = quillRef.current?.getEditor?.()?.root;

      if (!img || !editorRoot) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startY = event.clientY;
      const startRect = img.getBoundingClientRect();
      const startWidth = startRect.width;
      const startHeight = startRect.height;
      const editorWidth = editorRoot.getBoundingClientRect().width || MAX_IMAGE_WIDTH_PX;
      const maxWidth = Math.min(editorWidth, MAX_IMAGE_WIDTH_PX);
      const borderWidth = getImageBorderWidthFromNode(img);

      const handlePointerMove = (moveEvent) => {
        moveEvent.preventDefault();

        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;
        const nextWidth = Math.round(clampNumber(startWidth + deltaX, MIN_IMAGE_WIDTH_PX, maxWidth));
        const nextHeight = Math.round(
          clampNumber(startHeight + deltaY, MIN_IMAGE_HEIGHT_PX, MAX_IMAGE_HEIGHT_PX)
        );

        applyImageSettingsToNode(img, {
          width: `${nextWidth}px`,
          height: `${nextHeight}px`,
          borderWidth,
        });
        setImageWidthInput(String(nextWidth));
        setImageWidthUnit("px");
        setImageHeightInput(String(nextHeight));
        setImageBorderInput(String(borderWidth));
        setActiveImageMeta((prev) =>
          prev
            ? {
                ...prev,
                width: `${nextWidth}px`,
                widthAmount: String(nextWidth),
                widthUnit: "px",
                height: `${nextHeight}px`,
                borderWidth,
              }
            : prev
        );
        scheduleResizeOverlayUpdate();
      };

      const handlePointerUp = () => {
        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", handlePointerUp);
        selectEditorImage(img);
        syncEditorContentFromDom();
        setMessage("طول و عرض عکس با ماوس تغییر کرد.");
        setError("");
      };

      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp, { once: true });
    },
    [scheduleResizeOverlayUpdate, selectEditorImage, syncEditorContentFromDom]
  );

  useEffect(() => {
    if (!activeImageMeta) return undefined;

    scheduleResizeOverlayUpdate();

    const handleWindowChange = () => scheduleResizeOverlayUpdate();

    window.addEventListener("resize", handleWindowChange);
    window.addEventListener("scroll", handleWindowChange, true);

    return () => {
      window.removeEventListener("resize", handleWindowChange);
      window.removeEventListener("scroll", handleWindowChange, true);
    };
  }, [activeImageMeta, scheduleResizeOverlayUpdate]);

  const getRemovableImageWrapper = (img, editorRoot) => {
    const parent = img?.parentElement;

    if (!parent || parent === editorRoot) {
      return img;
    }

    if (!parent.matches("p, figure, div")) {
      return img;
    }

    const clone = parent.cloneNode(true);
    clone.querySelectorAll("img, br").forEach((node) => node.remove());

    if (clone.textContent.trim() === "" && parent.querySelectorAll("img").length === 1) {
      return parent;
    }

    return img;
  };

  const deleteActiveEditorImage = useCallback(() => {
    const img = activeImageRef.current;
    const editor = quillRef.current?.getEditor?.();
    const editorRoot = editor?.root;

    if (!img || !editorRoot) return false;

    try {
      const imageBlot = Quill.find(img);

      if (imageBlot) {
        const imageIndex = editor.getIndex(imageBlot);
        editor.deleteText(imageIndex, 1, "user");
        editor.setSelection(Math.max(imageIndex - 1, 0), 0, "silent");
      } else {
        const removableNode = getRemovableImageWrapper(img, editorRoot);
        removableNode?.remove?.();
        syncEditorContentFromDom();
      }

      clearActiveImage();
      setMessage("عکس انتخاب‌شده حذف شد.");
      setError("");
      return true;
    } catch (err) {
      console.error("Delete editor image error:", err);
      const removableNode = getRemovableImageWrapper(img, editorRoot);
      removableNode?.remove?.();
      syncEditorContentFromDom();
      clearActiveImage();
      return true;
    }
  }, [clearActiveImage, syncEditorContentFromDom]);

  const handleEditorKeyDown = useCallback(
    (event) => {
      if (!activeImageRef.current) return;

      if (event.target?.closest?.(".admin-image-inline-panel")) {
        return;
      }

      if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault();
        event.stopPropagation();
        deleteActiveEditorImage();
      }
    },
    [deleteActiveEditorImage]
  );

  const insertEmoji = (emoji) => {
    const editor = quillRef.current?.getEditor?.();

    if (!editor) return;

    const range = editor.getSelection(true);
    const insertIndex = range ? range.index : editor.getLength();

    editor.insertText(insertIndex, emoji, "user");
    editor.setSelection(insertIndex + emoji.length, 0);
  };

  const handleEditorImageUpload = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png,image/jpeg,image/jpg,image/webp";

    input.onchange = async () => {
      const file = input.files?.[0];

      if (!file) return;

      if (!file.type.startsWith("image/")) {
        setError("فقط فایل تصویری مجاز است.");
        return;
      }

      try {
        setUploadingImage(true);
        setError("");
        setMessage("");

        const data = await uploadAdminImage(file);

        if (!data?.imageUrl) {
          throw new Error("آدرس تصویر از سرور دریافت نشد.");
        }

        const editor = quillRef.current?.getEditor?.();

        if (!editor) {
          throw new Error("ویرایشگر آماده نیست. لطفاً دوباره تلاش کنید.");
        }

        const range = editor.getSelection(true);
        const insertIndex = range ? range.index : editor.getLength();

        editor.insertEmbed(
          insertIndex,
          "image",
          { src: data.imageUrl, alt: file.name, width: "100%", height: "auto", borderWidth: 0 },
          "user"
        );
        editor.insertText(insertIndex + 1, " ", "user");
        editor.setSelection(insertIndex + 2, 0);

        setFormData((prev) => ({
          ...prev,
          content: editor.root.innerHTML,
        }));

        clearActiveImage();
        setMessage("تصویر داخل متن اضافه شد؛ برای تغییر اندازه روی خود عکس کلیک و از گوشه آن بکشید.");
      } catch (err) {
        console.error("Editor image upload error:", err);
        setError(err.message || "خطا در افزودن تصویر به متن");
      } finally {
        setUploadingImage(false);
      }
    };

    input.click();
  }, [clearActiveImage]);

  const editorModules = useMemo(
    () => ({
      toolbar: {
        container: editorToolbar,
        handlers: {
          image: handleEditorImageUpload,
        },
      },
      history: {
        delay: 500,
        maxStack: 100,
        userOnly: true,
      },
    }),
    [handleEditorImageUpload]
  );

  const fetchContents = async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("staffToken");

      if (!token) {
        setError("دسترسی غیرمجاز است. لطفاً وارد پنل شوید.");
        setContents([]);
        return;
      }

      const data = await getAdminContents({ limit: 5 });

      setContents(data.contents || []);
    } catch (err) {
      console.error("Fetch admin contents error:", err);
      setError(err.message || "خطا در دریافت محتواها");
      setContents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContents();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "type") {
      setFormData((prev) => ({
        ...prev,
        type: value,
        category: "",
        level: value === "course" ? prev.level || "مقدماتی" : "",
        duration: value === "course" ? prev.duration : "",
      }));

      return;
    }

    if (name === "status") {
      setFormData((prev) => ({
        ...prev,
        status: value,
        publishAt: value === "scheduled" ? prev.publishAt : "",
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePublishAtChange = (dateObject) => {
    if (!dateObject) {
      setFormData((prev) => ({
        ...prev,
        publishAt: "",
      }));

      return;
    }

    const jsDate = dateObject.toDate();

    setFormData((prev) => ({
      ...prev,
      publishAt: formatDateTimeLocal(jsDate),
    }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setEmojiOpen(false);
    clearActiveImage();
    setMessage("");
    setError("");

    if (editIdFromArchive) {
      setSearchParams({});
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("فقط فایل تصویری مجاز است.");
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("حجم تصویر نباید بیشتر از ۵ مگابایت باشد.");
      e.target.value = "";
      return;
    }

    try {
      setUploadingImage(true);
      setError("");
      setMessage("");

      // مهم:
      // uploadAdminImage خودش FormData می‌سازد.
      // بنابراین باید خود فایل را بفرستیم، نه FormData.
      // تبدیل به WebP و فیت کردن تصویر در بک‌اند با Sharp انجام می‌شود.
      const data = await uploadAdminImage(file);

      if (!data?.imageUrl) {
        throw new Error("آدرس تصویر از سرور دریافت نشد.");
      }

      setFormData((prev) => ({
        ...prev,
        image: data.imageUrl,
      }));

      setMessage("تصویر با موفقیت آپلود و بهینه‌سازی شد.");
    } catch (err) {
      console.error("Upload image frontend error:", err);
      setError(err.message || "خطا در آپلود تصویر");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("staffToken");

    if (!token) {
      setError("دسترسی غیرمجاز است. لطفاً وارد پنل شوید.");
      return;
    }

    if (
      !formData.title ||
      !formData.type ||
      !formData.category ||
      !formData.tags ||
      !formData.summary ||
      !formData.content
    ) {
      setError(
        "لطفاً عنوان، نوع محتوا، دسته‌بندی، برچسب‌ها، خلاصه و متن اصلی را کامل کنید."
      );
      return;
    }

    if (formData.type === "course" && !formData.level) {
      setError("لطفاً سطح دوره را انتخاب کنید.");
      return;
    }

    if (formData.status === "scheduled") {
      if (!formData.publishAt) {
        setError("برای انتشار زمان‌بندی‌شده، زمان انتشار را انتخاب کنید.");
        return;
      }

      const selectedDate = new Date(formData.publishAt);

      if (Number.isNaN(selectedDate.getTime())) {
        setError("زمان انتشار معتبر نیست.");
        return;
      }

      if (selectedDate <= new Date()) {
        setError("برای انتشار زمان‌بندی‌شده، زمان انتشار باید در آینده باشد.");
        return;
      }
    }

    try {
      setSaving(true);
      setMessage("");
      setError("");

      const payload = {
        title: formData.title,
        type: formData.type,
        category: formData.category,
        tags: formData.tags,
        summary: formData.summary,
        content: cleanEditorHtml(formData.content),
        image: formData.image,
        status: formData.status,
        level: formData.type === "course" ? formData.level : "",
        duration: formData.type === "course" ? formData.duration : "",
        publishAt:
          formData.status === "scheduled" && formData.publishAt
            ? new Date(formData.publishAt).toISOString()
            : null,
      };

      if (editingId) {
        await updateAdminContent(editingId, payload);
        setMessage("محتوا با موفقیت ویرایش شد.");
      } else {
        await createAdminContent(payload);
        setMessage("محتوا با موفقیت ایجاد شد.");
      }

      resetForm();

      await fetchContents();
    } catch (err) {
      console.error("Save content error:", err);
      setError(err.message || "خطا در ذخیره محتوا");
    } finally {
      setSaving(false);
    }
  };

  const applyEditToForm = (item) => {
    setEditingId(item._id);

    setFormData({
      title: item.title || "",
      type: item.type || "news",
      category: item.category || "",
      tags: Array.isArray(item.tags)
        ? item.tags.map((tag) => `#${tag}`).join(" ")
        : "",
      summary: item.summary || "",
      content: item.content || "",
      image: item.image || "",
      status: item.status || "draft",
      level: item.level || "",
      duration: item.duration || "",
      publishAt:
        item.status === "scheduled" ? formatDateTimeLocal(item.publishAt) : "",
    });

    setEmojiOpen(false);
    clearActiveImage();
    setMessage("");
    setError("");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleEdit = (item) => {
    applyEditToForm(item);
  };

  useEffect(() => {
    if (!editIdFromArchive) return;

    const loadContentForEdit = async () => {
      try {
        setLoading(true);
        setError("");
        setMessage("");

        const data = await getAdminContentById(editIdFromArchive);
        const selectedContent = data?.content || data?.data?.content;

        if (!selectedContent) {
          throw new Error("محتوای انتخاب‌شده برای ویرایش پیدا نشد.");
        }

        applyEditToForm(selectedContent);
        setMessage("محتوا از آرشیو برای ویرایش باز شد.");
      } catch (err) {
        console.error("Load archive edit content error:", err);
        setError(err.message || "خطا در دریافت محتوا برای ویرایش");
      } finally {
        setLoading(false);
      }
    };

    loadContentForEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editIdFromArchive]);

  const handleDelete = async (id) => {
    const token = localStorage.getItem("staffToken");

    if (!token) {
      setError("دسترسی غیرمجاز است. لطفاً وارد پنل شوید.");
      return;
    }

    const confirmed = window.confirm("آیا از حذف این محتوا مطمئن هستید؟");

    if (!confirmed) return;

    try {
      setMessage("");
      setError("");

      await deleteAdminContent(id);
      setMessage("محتوا با موفقیت حذف شد.");
      await fetchContents();
    } catch (err) {
      setError(err.message || "خطا در حذف محتوا");
    }
  };

  const getStatusLabel = (status) => {
    if (status === "published") return "منتشر شده";
    if (status === "scheduled") return "زمان‌بندی‌شده";
    return "پیش‌نویس";
  };

  const getStatusClass = (status) => {
    if (status === "published") return "status-pill published";
    if (status === "scheduled") return "status-pill scheduled";
    return "status-pill draft";
  };

  const getTypeLabel = (type) => {
    if (type === "course") return "دوره";
    return "خبر";
  };

  return (
    <div className="admin-page">
      <div className="admin-shell">
        <div className="admin-page-header">
          <div>
            <span className="admin-badge">پنل مدیریت</span>
            <h1>مدیریت اخبار و دوره‌ها</h1>
            <p>
              از این بخش می‌توانید خبر یا دوره جدید بسازید، ویرایش کنید، حذف
              کنید یا وضعیت انتشار آن را تغییر دهید.
            </p>
          </div>

          <div className="admin-header-actions">
            <Link to="/admin/content/archive" className="admin-back-link">
              آرشیو مطالب
            </Link>

            <Link to="/admin/dashboard" className="admin-back-link">
              بازگشت به داشبورد
            </Link>
          </div>
        </div>

        {message && <div className="admin-message success">{message}</div>}
        {error && <div className="admin-message error">{error}</div>}

        <section className="admin-content-layout">
          <form className="admin-content-form" onSubmit={handleSubmit}>
            <h2>{editingId ? "ویرایش محتوا" : "افزودن محتوای جدید"}</h2>

            <div className="admin-form-grid">
              <div className="admin-field">
                <label>عنوان</label>
                <input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="مثلاً تاثیر هوش مصنوعی در آموزش"
                  required
                />
              </div>

              <div className="admin-field">
                <label>نوع محتوا</label>
                <CustomSelect
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  placeholder="انتخاب نوع محتوا"
                  options={[
                    { value: "news", label: "خبر" },
                    { value: "course", label: "دوره آموزشی" },
                  ]}
                />
              </div>

              <div className="admin-field">
                <label>دسته‌بندی</label>
                <CustomSelect
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  placeholder="انتخاب دسته‌بندی"
                  options={currentCategories.map((category) => ({
                    value: category,
                    label: category,
                  }))}
                />
              </div>

              <div className="admin-field admin-full">
                <label>
                  برچسب‌ها
                  <span className="admin-required-mark"> الزامی</span>
                </label>

                <input
                  name="tags"
                  value={formData.tags}
                  onChange={handleChange}
                  placeholder="مثلاً #هوش_مصنوعی #آموزش #امنیت_سایبری"
                  required
                />

                <small className="admin-field-help">
                  برچسب‌ها را با فاصله، کاما یا علامت # جدا کنید.
                </small>
              </div>

              <div className="admin-field">
                <label>وضعیت انتشار</label>
                <CustomSelect
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  placeholder="انتخاب وضعیت انتشار"
                  options={statusOptions}
                />
              </div>

              {formData.status === "scheduled" && (
                <div className="admin-field">
                  <label>زمان انتشار زمان‌بندی‌شده</label>

                  <DatePicker
                    value={
                      formData.publishAt ? new Date(formData.publishAt) : null
                    }
                    onChange={handlePublishAtChange}
                    calendar={persian}
                    locale={persian_fa}
                    format="YYYY/MM/DD HH:mm"
                    calendarPosition="bottom-right"
                    inputClass="admin-persian-datepicker-input"
                    containerClassName="admin-persian-datepicker-container"
                    portal
                    plugins={[
                      <TimePicker
                        key="time-picker"
                        position="bottom"
                        hideSeconds
                      />,
                    ]}
                  />

                  <small className="admin-field-help">
                    این محتوا در تاریخ و ساعت انتخاب‌شده به‌صورت خودکار منتشر
                    می‌شود.
                  </small>
                </div>
              )}

              <div className="admin-field admin-full">
  <label>تصویر محتوا</label>

  <div className="admin-upload-box">
    <input
      type="file"
      accept="image/png,image/jpeg,image/jpg,image/webp"
      onChange={handleImageUpload}
      disabled={uploadingImage}
    />

    <div className="admin-upload-info">
      <strong>
        {uploadingImage ? "در حال آپلود و بهینه‌سازی..." : "انتخاب تصویر"}
      </strong>

      <span>
        فرمت‌های مجاز: JPG, PNG, WEBP — خروجی به‌صورت WebP و زیر ۷۰ کیلوبایت ذخیره می‌شود
      </span>
    </div>
  </div>

  {formData.image && (
    <div className="admin-upload-url">
      <span>آدرس تصویر بهینه‌شده:</span>
      <small>{formData.image}</small>
    </div>
  )}
</div>

              {formData.type === "course" && (
                <>
                  <div className="admin-field">
                    <label>سطح دوره</label>
                    <CustomSelect
                      name="level"
                      value={formData.level}
                      onChange={handleChange}
                      placeholder="انتخاب سطح دوره"
                      options={courseLevels.map((level) => ({
                        value: level,
                        label: level,
                      }))}
                    />
                  </div>

                  <div className="admin-field">
                    <label>مدت زمان دوره</label>
                    <input
                      name="duration"
                      value={formData.duration}
                      onChange={handleChange}
                      placeholder="مثلاً ۳۰ ساعت"
                    />
                  </div>
                </>
              )}

              {formData.image && (
                <div className="admin-image-preview admin-full">
                  <span>پیش‌نمایش تصویر</span>
                  <img src={formData.image} alt="preview" />
                </div>
              )}

              <div className="admin-field admin-full">
                <label>خلاصه محتوا</label>
                <textarea
                  name="summary"
                  value={formData.summary}
                  onChange={handleChange}
                  placeholder="یک خلاصه کوتاه برای نمایش در کارت محتوا"
                  required
                />
              </div>

              <div className="admin-field admin-full">
                <label>متن اصلی</label>

                <div
                  ref={richEditorRef}
                  className="admin-rich-editor"
                  onClickCapture={handleEditorAreaClick}
                  onKeyDownCapture={handleEditorKeyDown}
                >
                  <div
                    className={`admin-emoji-package ${
                      emojiOpen ? "open" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="admin-emoji-package-trigger"
                      onClick={() => setEmojiOpen((prev) => !prev)}
                    >
                      <span>Emoji</span>
                      <span>😊</span>
                    </button>

                    {emojiOpen && (
                      <div className="admin-emoji-panel">
                        {editorEmojis.map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            className="admin-emoji-btn"
                            onClick={() => insertEmoji(emoji)}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={formData.content}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        content: value,
                      }))
                    }
                    modules={editorModules}
                    formats={editorFormats}
                    placeholder="متن کامل خبر یا توضیحات دوره را وارد کنید"
                  />

                  {resizeOverlay && activeImageMeta && (
                    <div
                      className="admin-image-resize-overlay"
                      style={{
                        left: resizeOverlay.left,
                        top: resizeOverlay.top,
                        width: resizeOverlay.width,
                        height: resizeOverlay.height,
                      }}
                    >
                      <div className="admin-image-inline-panel">
                        <label className="admin-image-inline-field">
                          <span>عرض</span>
                          <span className="admin-image-inline-control">
                            <input
                              type="number"
                              min={imageWidthUnit === "%" ? MIN_IMAGE_WIDTH_PERCENT : MIN_IMAGE_WIDTH_PX}
                              max={imageWidthUnit === "%" ? MAX_IMAGE_WIDTH_PERCENT : MAX_IMAGE_WIDTH_PX}
                              value={imageWidthInput}
                              onChange={(event) => setImageWidthInput(event.target.value)}
                              onKeyDown={(event) => event.stopPropagation()}
                            />
                            <select
                              className="admin-image-width-unit-select"
                              value={imageWidthUnit}
                              onChange={(event) => setImageWidthUnit(event.target.value)}
                              onKeyDown={(event) => event.stopPropagation()}
                            >
                              <option value="%">%</option>
                              <option value="px">px</option>
                            </select>
                          </span>
                        </label>

                        <label className="admin-image-inline-field">
                          <span>ارتفاع</span>
                          <span className="admin-image-inline-control">
                            <input
                              type="number"
                              min={MIN_IMAGE_HEIGHT_PX}
                              max={MAX_IMAGE_HEIGHT_PX}
                              placeholder="auto"
                              value={imageHeightInput}
                              onChange={(event) => setImageHeightInput(event.target.value)}
                              onKeyDown={(event) => event.stopPropagation()}
                            />
                            <span className="admin-image-inline-unit">px</span>
                          </span>
                        </label>

                        <label className="admin-image-inline-field">
                          <span>حاشیه</span>
                          <span className="admin-image-inline-control">
                            <input
                              type="number"
                              min={MIN_IMAGE_BORDER_PX}
                              max={MAX_IMAGE_BORDER_PX}
                              value={imageBorderInput}
                              onChange={(event) => setImageBorderInput(event.target.value)}
                              onKeyDown={(event) => event.stopPropagation()}
                            />
                            <span className="admin-image-inline-unit">px</span>
                          </span>
                        </label>

                        <button type="button" onClick={applyManualImageSettings}>
                          اعمال
                        </button>
                      </div>

                      <button
                        type="button"
                        className="admin-image-resize-handle"
                        onPointerDown={handleImageResizePointerDown}
                        aria-label="تغییر اندازه تصویر با ماوس"
                        title="برای تغییر اندازه عکس، بکشید؛ برای حذف Backspace یا Delete را بزنید"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="admin-form-actions">
              <button
                type="submit"
                className="admin-primary-btn"
                disabled={saving}
              >
                {saving
                  ? "در حال ذخیره..."
                  : editingId
                  ? "ذخیره ویرایش"
                  : "ایجاد محتوا"}
              </button>

              {editingId && (
                <button
                  type="button"
                  className="admin-secondary-btn"
                  onClick={resetForm}
                >
                  لغو ویرایش
                </button>
              )}
            </div>
          </form>

          <section className="admin-content-list">
            <div className="admin-list-header">
              <h2>۵ محتوای آخر</h2>

              <div className="admin-list-actions">
                <Link
                  to="/admin/content/archive"
                  className="admin-secondary-btn"
                >
                  مشاهده آرشیو کامل
                </Link>

                <button
                  type="button"
                  onClick={fetchContents}
                  className="admin-secondary-btn"
                >
                  بروزرسانی
                </button>
              </div>
            </div>

            {loading && (
              <div className="admin-empty">در حال دریافت محتواها...</div>
            )}

            {!loading && contents.length === 0 && (
              <div className="admin-empty">هنوز محتوایی ثبت نشده است.</div>
            )}

            {!loading && contents.length > 0 && (
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>عنوان</th>
                      <th>نوع</th>
                      <th>وضعیت</th>
                      <th>زمان انتشار</th>
                      <th>دسته‌بندی</th>
                      <th>عملیات</th>
                    </tr>
                  </thead>

                  <tbody>
                    {visibleContents.map((item) => (
                      <tr key={item._id}>
                        <td>
                          <strong>{item.title}</strong>
                          <span>{item.slug}</span>

                          {Array.isArray(item.tags) &&
                            item.tags.length > 0 && (
                              <div className="admin-table-tags">
                                {item.tags.slice(0, 3).map((tag) => (
                                  <small key={tag}>#{tag}</small>
                                ))}
                              </div>
                            )}
                        </td>

                        <td>{getTypeLabel(item.type)}</td>

                        <td>
                          <span className={getStatusClass(item.status)}>
                            {getStatusLabel(item.status)}
                          </span>
                        </td>

                        <td>
                          {item.publishAt ? (
                            <small className="admin-scheduled-time">
                              {formatPersianDateTime(item.publishAt)}
                            </small>
                          ) : (
                            <small className="admin-scheduled-time">
                              بدون زمان
                            </small>
                          )}
                        </td>

                        <td>{item.category}</td>

                        <td>
                          <div className="admin-row-actions">
                            <button
                              type="button"
                              onClick={() => handleEdit(item)}
                              className="admin-edit-btn"
                            >
                              ویرایش
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDelete(item._id)}
                              className="admin-delete-btn"
                            >
                              حذف
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </section>
      </div>
    </div>
  );
}

export default AdminContentManager;